// Pipeline-Board (/opportunities): Filterpillen und Bedienleiste oben, darunter die Spalten. Die Wahl „nur aktive“, die
// Kartenangaben und die Sortierung merkt sich der Server je Nutzer (`opportunities:board`).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type Board, type OpportunityZeile, type OpportunitySortierung } from "../api/opportunities";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Bedienleiste } from "../components/smartview/Bedienleiste";
import { ListenSchluessel } from "../components/smartview/listenVertrag";
import { useMeta } from "../daten/meta";
import { useSeitenBefehle, useSeitenKopf } from "../daten/seite";
import { T } from "../texte";
import { ansichtLesen, ansichtMerken } from "./ansicht";
import { OpportunityBoard } from "./Board";
import { useBoard, type BoardParameter } from "./daten";
import { FilterLeiste } from "./FilterLeiste";
import { STANDARD_KARTENOPTIONEN, type KartenOptionen } from "./Karte";
import { OpportunityKopf } from "./Kopf";
import { apiAlsSort, karteVerschieben, sortAlsApi } from "./logik";
import { OpportunityDialog } from "./OpportunityDialog";
import { OptionenKnopf } from "./OptionenKnopf";
import { SORT_FELDER } from "./spalten";
import { useDarfAendern, useStatusWechsel } from "./statuswechsel";
import { useFilter } from "./zustand";
import "./opportunities.css";

const LISTE = ListenSchluessel.opportunitiesBoard;
const STANDARD_SORT = "-value";

interface Anfang {
  nurAktive: boolean;
  karten: KartenOptionen;
  sort: string;
}

export function BoardSeite() {
  const [anfang, setAnfang] = useState<Anfang | null>(null);
  useSeitenKopf([{ label: T.nav.opportunities }]);
  useEffect(() => {
    let weg = false;
    void ansichtLesen(LISTE).then((a) => {
      if (weg) return;
      const e = a.einstellungen as { nurAktive?: unknown; karten?: Partial<KartenOptionen> };
      const s = a.sortierung?.[0];
      setAnfang({
        nurAktive: typeof e.nurAktive === "boolean" ? e.nurAktive : true,
        karten: { ...STANDARD_KARTENOPTIONEN, ...(e.karten ?? {}) },
        sort: s ? sortAlsApi(s, STANDARD_SORT) : STANDARD_SORT,
      });
    });
    return () => {
      weg = true;
    };
  }, []);
  return anfang ? <BoardInhalt anfang={anfang} /> : <Laden />;
}

function BoardInhalt({ anfang }: { anfang: Anfang }) {
  const { meta } = useMeta();
  const darfAendern = useDarfAendern();
  const wechseln = useStatusWechsel();
  const { werte, setze, zuruecksetzen, aktiv, suche } = useFilter();
  const [nurAktive, setNurAktive] = useState(anfang.nurAktive);
  const [karten, setKarten] = useState(anfang.karten);
  const [sort, setSort] = useState(anfang.sort);
  const [dialog, setDialog] = useState<{ opp: OpportunityZeile | null; statusId?: string | null } | null>(null);
  const jeSpalte = useRef(30);

  const parameter = useMemo<BoardParameter>(
    () => ({
      pipeline_id: werte.pipeline ?? undefined,
      user_id: werte.nutzer ?? undefined,
      smart_view_id: werte.view ?? undefined,
      feld: werte.feld,
      von: werte.zeitraum.von ?? undefined,
      bis: werte.zeitraum.bis ?? undefined,
      status_type: nurAktive ? "active" : "all",
      sort: sort as OpportunitySortierung,
    }),
    [werte.pipeline, werte.nutzer, werte.view, werte.feld, werte.zeitraum.von, werte.zeitraum.bis, nurAktive, sort],
  );
  const { board, setBoard, laedt, fehler, neuLaden, still } = useBoard(parameter, () => jeSpalte.current);
  const boardRef = useRef<Board | null>(null);
  boardRef.current = board;

  const gesamt = board ? board.spalten.reduce((n, s) => n + s.anzahl, 0) : null;

  const neu = useCallback((statusId?: string | null) => setDialog({ opp: null, statusId }), []);
  useSeitenBefehle([{ id: "opp-neu", label: "Neue Opportunity", icon: "plus", gruppe: "Opportunities", aktion: () => neu() }]);

  const verschieben = useCallback(
    (opp: OpportunityZeile, statusId: string) => {
      const vorher = boardRef.current;
      return wechseln(opp, statusId, {
        sofort: (o, ziel) => setBoard((b) => (b ? { ...b, spalten: karteVerschieben(b.spalten, o.id, ziel) } : b)),
        rollback: () => {
          if (vorher) setBoard(vorher);
          still();
        },
        fertig: () => still(),
      });
    },
    [wechseln, setBoard, still],
  );

  const merkenEinstellung = (teil: Record<string, unknown>) => void ansichtMerken(LISTE, { einstellungen: teil });
  const sortieren = (s: { key: string; ab: boolean } | null) => {
    setSort(sortAlsApi(s, STANDARD_SORT));
    void ansichtMerken(LISTE, { sortierung: s ? [s] : null });
  };

  return (
    <div className="liste-seite">
      <OpportunityKopf titel={board?.pipeline ? `${board.pipeline.name} Pipeline` : "Pipeline"} suche={suche} onNeu={() => neu()} />
      <Bedienleiste
        liste={LISTE}
        einheit={{ eins: "Opportunity", viele: "Opportunities" }}
        trefferZahl={gesamt}
        laedt={laedt}
        sort={apiAlsSort(sort)}
        sortFelder={SORT_FELDER}
        onSort={sortieren}
        vorn={
          <FilterLeiste
            werte={werte}
            pipelines={meta?.pipelines ?? []}
            pipelineId={board?.pipeline?.id ?? werte.pipeline}
            onAendern={setze}
            onZuruecksetzen={zuruecksetzen}
            aktiv={aktiv}
          />
        }
      >
        <span className="liste-leiste__rechts">
          <OptionenKnopf
            nurAktive={nurAktive}
            onNurAktive={(an) => {
              setNurAktive(an);
              merkenEinstellung({ nurAktive: an });
            }}
            optionen={karten}
            onOptionen={(o) => {
              setKarten(o);
              merkenEinstellung({ karten: o });
            }}
          />
        </span>
      </Bedienleiste>
      {fehler && !board ? <FehlerBox fehler={fehler} onNochmal={() => void neuLaden()} /> : null}
      {!board ? (
        fehler ? null : <Laden />
      ) : !board.pipeline ? (
        <Leer titel="Es gibt noch keine Pipeline." text="Pipelines kommen mit dem Abgleich aus Close." />
      ) : (
        <OpportunityBoard
          board={board}
          setBoard={setBoard}
          parameter={parameter}
          optionen={karten}
          darfAendern={darfAendern}
          onVerschieben={(opp, id) => void verschieben(opp, id)}
          onBearbeiten={(opp) => setDialog({ opp })}
          onNeu={(id) => neu(id)}
          onTiefe={(n) => {
            jeSpalte.current = Math.min(100, Math.max(jeSpalte.current, n));
          }}
        />
      )}
      <OpportunityDialog
        offen={dialog !== null}
        onSchliessen={() => setDialog(null)}
        opportunity={dialog?.opp ?? null}
        statusId={dialog?.statusId ?? null}
        onGespeichert={() => still()}
      />
    </div>
  );
}
